import { SWRFetcher } from '@commenda/utils';
import type { Currency } from '@prisma/client';
import useSWR from 'swr';

export interface AdminExternalConnectionCompany {
  id: number;
  displayName: string | null;
  preferredBillingCurrency: Currency | null;
}

export interface AdminPylonAccountSummary {
  id: string;
  name: string;
  primaryDomain: string | null;
  domain: string | null;
  domains: string[];
  ownerEmail: string | null;
  ownerId: string | null;
  type: string | null;
  createdAt: string | null;
  externalIds: { externalId: string; label: string }[];
}

export interface AdminPylonConnectionState {
  accountId: string | null;
  account: AdminPylonAccountSummary | null;
  accountLookupError?: string;
}

export interface AdminChargebeeCustomerSummary {
  id: string;
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  businessEntityId: string | null;
  businessEntityLabel: string;
  businessEntityCurrency: Currency | null;
  chargebeeCreatedAt: number | null;
  linkedCompany: { id: number; name: string | null } | null;
  linkedAffiliateFirm: { id: string; name: string | null } | null;
}

export interface AdminChargebeeCustomerConnection
  extends AdminChargebeeCustomerSummary {
  billingCustomerRowId: string;
  billingCustomerId: string;
  linkedAt: string;
  lastUpdatedAt: string;
  lookupError?: string;
}

export interface AdminChargebeeConnectionState {
  preferredBillingCurrency: Currency | null;
  customers: AdminChargebeeCustomerConnection[];
}

export interface AdminSlackChannelSummary {
  id: string;
  name: string;
  isPrivate: boolean;
  isArchived: boolean;
  isBotMemberInChannel: boolean;
}

export interface AdminSlackConnectionState {
  channelId: string | null;
  channel: AdminSlackChannelSummary | null;
  channelLookupError?: string;
}

export interface AdminHubSpotCompanySummary {
  id: string;
  name: string | null;
  domain: string | null;
  website: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  linkedCompany: { id: number; name: string | null } | null;
}

export interface AdminHubSpotConnectionState {
  companyId: string | null;
  company: AdminHubSpotCompanySummary | null;
  companyLookupError?: string;
}

export interface AdminCompanyExternalConnectionsResponse {
  company: AdminExternalConnectionCompany;
  pylon: AdminPylonConnectionState;
  hubspot: AdminHubSpotConnectionState;
  chargebee: AdminChargebeeConnectionState;
  slack: AdminSlackConnectionState;
}

export const getAdminCompanyExternalConnectionsKey = (
  companyId: number | null
) => (companyId ? `admin/companies/${companyId}/external-connections` : null);

export function useAdminCompanyExternalConnections(companyId: number | null) {
  const key = getAdminCompanyExternalConnectionsKey(companyId);

  const { data, error, mutate, isLoading, isValidating } =
    useSWR<AdminCompanyExternalConnectionsResponse>(
      key,
      key ? SWRFetcher({ method: 'GET' }, undefined) : null
    );

  return {
    data: data ?? null,
    error,
    mutate,
    isLoading: !!isLoading,
    isValidating: !!isValidating,
  };
}
